export interface HotlistTimeParams {
  date?: string;
  timeid?: number;
  at?: string;
  minuteOfDay?: number;
  page: number;
}

export interface TimeSlot {
  timeid: number;
  timestamp: Date;
}

export interface HourTimelineItem {
  hour: number;
  count: number;
  selected: boolean;
}

function safePositiveInt(value: string | null, fallback?: number): number | undefined {
  if (!value) return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export function parseTimeOfDay(value: string | null): number | undefined {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) return undefined;
  const [hour, minute] = value.split(':').map((part) => Number.parseInt(part, 10));
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return undefined;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return undefined;
  return hour * 60 + minute;
}

export function parseHotlistTimeParams(params: URLSearchParams): HotlistTimeParams {
  const at = params.get('at') || undefined;
  const minuteOfDay = parseTimeOfDay(at || null);
  return {
    date: params.get('date') || undefined,
    timeid: safePositiveInt(params.get('timeid')),
    at: minuteOfDay === undefined ? undefined : at,
    minuteOfDay,
    page: safePositiveInt(params.get('page'), 1) || 1,
  };
}

export function buildDateRange(date: string): { startDate: Date; endDate: Date } {
  return {
    startDate: new Date(`${date}T00:00:00+08:00`),
    endDate: new Date(`${date}T23:59:59.999+08:00`),
  };
}

export function formatDateParam(date: Date): string {
  const parts = partsInBeijing(date);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function partsInBeijing(date: Date): Record<string, string> {
  const parts = new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
}

export function formatBeijingTime(date: Date): string {
  const parts = partsInBeijing(date);
  return `${parts.hour}:${parts.minute}`;
}

export function formatBeijingDateTime(date: Date): string {
  const parts = partsInBeijing(date);
  return `${parts.year}/${parts.month}/${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
}

function minuteOfDayInBeijing(date: Date): number {
  return parseTimeOfDay(formatBeijingTime(date)) || 0;
}

export function filterTimeSlotsByHour<T extends TimeSlot>(slots: T[], hour?: number): T[] {
  const filtered =
    hour === undefined
      ? slots
      : slots.filter((slot) => Math.floor(minuteOfDayInBeijing(slot.timestamp) / 60) === hour);
  return [...filtered].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

export function getHourlyTimeline<T extends TimeSlot>(
  slots: T[],
  selectedTimeId?: number
): HourTimelineItem[] {
  const selectedSlot = slots.find((slot) => slot.timeid === selectedTimeId);
  const selectedHour = selectedSlot
    ? Math.floor(minuteOfDayInBeijing(selectedSlot.timestamp) / 60)
    : undefined;
  return Array.from({ length: 24 }, (_, hour) => ({
    hour,
    count: slots.filter((slot) => Math.floor(minuteOfDayInBeijing(slot.timestamp) / 60) === hour).length,
    selected: selectedHour === hour,
  }));
}

export function getAdjacentSlots<T extends TimeSlot>(
  newestFirstSlots: T[],
  selectedTimeId?: number
): { newer?: T; older?: T } {
  const index = newestFirstSlots.findIndex((slot) => slot.timeid === selectedTimeId);
  if (index < 0) return {};
  return {
    newer: index > 0 ? newestFirstSlots[index - 1] : undefined,
    older: newestFirstSlots[index + 1],
  };
}

export function getNearestTimeSlot<T extends TimeSlot>(
  slots: T[],
  minuteOfDay: number
): T | undefined {
  if (slots.length === 0) return undefined;
  return [...slots].sort((a, b) => {
    const aMinute = minuteOfDayInBeijing(a.timestamp);
    const bMinute = minuteOfDayInBeijing(b.timestamp);
    const aDistance = Math.abs(aMinute - minuteOfDay);
    const bDistance = Math.abs(bMinute - minuteOfDay);

    if (aDistance !== bDistance) return aDistance - bDistance;
    return aMinute - bMinute;
  })[0];
}
